import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider, Box, CssBaseline, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Alert, TextField, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import axiosPublic from '../../api/axiosPublic';
import WebHeader from './WebHeader';
import WebFooter from './WebFooter';
import publicTheme from './publicTheme';
import ScrollToTop from './ScrollToTop';
import AiAdmissionsChatbot from './AiAdmissionsChatbot';
import { getRootSubdomain } from '../utils/subdomain';
import { PUBLIC_BRAND_THEME } from './publicBrandTheme';
import {
  buildPublicSchoolProfile,
  PUBLIC_SCHOOL_PROFILE_STORAGE_KEY,
} from './publicSchoolProfile';

const PublicLayout = () => {
  const location = useLocation();
  const [isSwitching, setIsSwitching] = useState(false);
  const [showIntro] = useState(false);
  const [websiteRequestSettings, setWebsiteRequestSettings] = useState({
    admissionsPopupEnabled: false,
    customMessageEnabled: false,
    customMessageText: '',
  });
  const [showAdmissionsPrompt, setShowAdmissionsPrompt] = useState(false);
  const [admissionsSubmitting, setAdmissionsSubmitting] = useState(false);
  const [admissionsForm, setAdmissionsForm] = useState({
    parentName: '',
    studentName: '',
    grade: '',
    phone: '',
    email: '',
    message: '',
  });
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const tenantKey = getRootSubdomain();
  const admissionsPromptStorageKey = tenantKey
    ? `website-admissions-prompt-shown:${tenantKey}`
    : 'website-admissions-prompt-shown';
  const isHomeRoute =
    location.pathname === '/' ||
    location.pathname === '/home' ||
    location.pathname === '/preview-home';

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarState({ open: true, message, severity });
  };

  const handleAdmissionsFormChange = (field, value) => {
    setAdmissionsForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAdmissionsSubmit = async (event) => {
    event.preventDefault();
    if (!tenantKey) {
      showSnackbar('Website context is missing.', 'error');
      return;
    }

    setAdmissionsSubmitting(true);
    try {
      await axiosPublic.post(`/api/${tenantKey}/website-requests`, {
        requestType: 'ADMISSION',
        parentName: admissionsForm.parentName.trim(),
        name: admissionsForm.parentName.trim(),
        studentName: admissionsForm.studentName.trim(),
        grade: admissionsForm.grade.trim(),
        phone: admissionsForm.phone.trim(),
        email: admissionsForm.email.trim(),
        message: admissionsForm.message.trim(),
        sourcePage: typeof window !== 'undefined' ? window.location.pathname : '',
      }, {
        skipGlobalLoader: true,
      });

      setAdmissionsForm({
        parentName: '',
        studentName: '',
        grade: '',
        phone: '',
        email: '',
        message: '',
      });
      setShowAdmissionsPrompt(false);
      showSnackbar('Admission request submitted successfully.', 'success');
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Failed to submit admission request.', 'error');
    } finally {
      setAdmissionsSubmitting(false);
    }
  };
  useEffect(() => {
    if (!tenantKey || typeof window === 'undefined') {
      return;
    }

    let active = true;

    const syncTenantContext = async () => {
      try {
        const response = await axiosPublic.get(`/api/${tenantKey}/context`, {
          skipGlobalLoader: true,
          skipGlobalError: true,
        });
        if (!active) {
          return;
        }

        const context = response?.data || {};
        let resolvedSchoolId = context.schoolId != null ? String(context.schoolId) : '';
        if (context.schoolId != null) {
          localStorage.setItem('schoolId', resolvedSchoolId);
        }
        if (context.schoolName) {
          localStorage.setItem('schoolName', context.schoolName);
        }

        const resolvedTenant = (context.subdomain || tenantKey || '').toString().trim().toLowerCase();
        if (resolvedTenant) {
          localStorage.setItem('tenantKey', resolvedTenant);
          localStorage.setItem('previewTenant', resolvedTenant);
        }

        if (resolvedSchoolId) {
          try {
            const schoolResponse = await axiosPublic.get(`/api/schools/${resolvedSchoolId}`, {
              skipGlobalLoader: true,
              skipGlobalError: true,
            });
            if (!active) {
              return;
            }
            const profile = buildPublicSchoolProfile(schoolResponse?.data || {});
            localStorage.setItem(PUBLIC_SCHOOL_PROFILE_STORAGE_KEY, JSON.stringify(profile));
            if (profile.name) {
              localStorage.setItem('schoolName', profile.name);
            }
            if (profile.logoUrl) {
              localStorage.setItem('logoUrl', profile.logoUrl);
            }
            window.dispatchEvent(new Event('public-school-profile-updated'));
          } catch (_error) {
            // Silent failure keeps existing public profile fallback values.
          }
        }
      } catch (_error) {
        // Silent failure keeps the public shell empty-first when no tenant context exists.
      }
    };

    syncTenantContext();

    return () => {
      active = false;
    };
  }, [tenantKey]);

  useEffect(() => {
    if (!tenantKey) {
      setWebsiteRequestSettings({
        admissionsPopupEnabled: false,
        customMessageEnabled: false,
        customMessageText: '',
      });
      setShowAdmissionsPrompt(false);
      return;
    }

    let active = true;

    const fetchWebsiteSettings = async () => {
      try {
        const response = await axiosPublic.get(`/api/${tenantKey}/website-requests/settings`, {
          skipGlobalLoader: true,
          skipGlobalError: true,
        });
        if (!active) {
          return;
        }

        const data = response?.data || {};
        const nextSettings = {
          admissionsPopupEnabled: String(data.website_admissions_popup_enabled || 'false') === 'true',
          customMessageEnabled: String(data.website_custom_message_enabled || 'false') === 'true',
          customMessageText: String(data.website_custom_message_text || ''),
        };

        setWebsiteRequestSettings(nextSettings);
      } catch (_error) {
        if (!active) {
          return;
        }
        setWebsiteRequestSettings({
          admissionsPopupEnabled: false,
          customMessageEnabled: false,
          customMessageText: '',
        });
        setShowAdmissionsPrompt(false);
      }
    };

    fetchWebsiteSettings();

    return () => {
      active = false;
    };
  }, [tenantKey]);

  useEffect(() => {
    if (!websiteRequestSettings.admissionsPopupEnabled) {
      setShowAdmissionsPrompt(false);
      return;
    }

    if (!isHomeRoute) {
      setShowAdmissionsPrompt(false);
      return;
    }

    if (typeof window !== 'undefined' && localStorage.getItem(admissionsPromptStorageKey) === 'true') {
      setShowAdmissionsPrompt(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowAdmissionsPrompt(true);
      if (typeof window !== 'undefined') {
        localStorage.setItem(admissionsPromptStorageKey, 'true');
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [admissionsPromptStorageKey, isHomeRoute, websiteRequestSettings.admissionsPopupEnabled]);

  useEffect(() => {
    setIsSwitching(true);
    const timer = setTimeout(() => {
      setIsSwitching(false);
    }, 140);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const introTitle =
    (typeof window !== 'undefined' && localStorage.getItem('schoolName')) || 'School Website';

  return (
    <ThemeProvider theme={publicTheme}>
      <CssBaseline />
      <ScrollToTop />
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflowX: 'hidden',
          background: PUBLIC_BRAND_THEME.pageBackground,
          '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle at 88% 8%, rgba(217,138,43,0.12), transparent 18%), radial-gradient(circle at 12% 0%, rgba(162,13,20,0.08), transparent 24%)',
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: '#FFFFFF',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 3,
          }}
        >
          <WebHeader />
        <AnimatePresence>
          {websiteRequestSettings.customMessageEnabled && websiteRequestSettings.customMessageText && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28 }}
              sx={{
                position: 'relative',
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.2,
                px: { xs: 1.5, md: 3 },
                py: 1.1,
                background: 'linear-gradient(90deg, #059669 0%, #10B981 52%, #047857 100%)',
                color: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              <CampaignRoundedIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.84rem', md: '0.92rem' } }}>
                {websiteRequestSettings.customMessageText}
              </Typography>
            </Box>
          )}
        </AnimatePresence>
        <Box
          component="main"
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: '1 0 auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: {
              xs: 'calc(100vh - 180px)',
              md: 'calc(100vh - 210px)',
            },
            pt: 0,
            pr: { xs: 0, md: 0 },
          }}
        >
          <Suspense fallback={null}>
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10, filter: 'blur(3px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -6, filter: 'blur(2px)' }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                style={{ position: 'relative', zIndex: 1, minHeight: '100%' }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </Suspense>
        </Box>
        <AnimatePresence>
          {isSwitching && (
            <>
              <Box
                component={motion.div}
                initial={{ scaleX: 0, opacity: 0.35 }}
                animate={{ scaleX: 1, opacity: 1 }}
                exit={{ scaleX: 0, opacity: 0 }}
                transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  transformOrigin: 'left center',
                  zIndex: 1500,
                  pointerEvents: 'none',
                  background: 'linear-gradient(90deg, #059669 0%, #10B981 48%, #F59E0B 100%)',
                  boxShadow: '0 6px 18px rgba(15,23,42,0.14)',
                }}
              />
            </>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showIntro && (
            <Box
              component={motion.div}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.5 } }}
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 1800,
                background:
                  'radial-gradient(circle at 18% -10%, rgba(56,217,169,0.3), transparent 38%), radial-gradient(circle at 100% 0%, rgba(14,165,233,0.24), transparent 42%), linear-gradient(160deg, #0B1220 0%, #0F172A 48%, #1E293B 130%)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <motion.div
                initial={{ y: 20, opacity: 0, scale: 0.96 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                style={{ textAlign: 'center', color: '#F8FAFF' }}
              >
                <Box
                  component={motion.div}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 2.4, ease: 'linear' }}
                  sx={{
                    width: 46,
                    height: 46,
                    marginInline: 'auto',
                    mb: 1.2,
                    borderRadius: '50%',
                    border: '2px solid rgba(248,250,255,0.24)',
                    borderTopColor: '#10B981',
                  }}
                />
                <Box
                  component={motion.div}
                  animate={{ opacity: [0.42, 1, 0.42] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  sx={{
                    fontFamily: '"Yantramanav", "Roboto", sans-serif',
                    fontSize: { xs: '1.25rem', md: '1.7rem' },
                    fontWeight: 790,
                    letterSpacing: '-0.01em',
                    mb: 0.35,
                  }}
                >
                  {introTitle}
                </Box>
                <Box sx={{ color: 'rgba(248,250,255,0.88)', fontSize: '0.86rem', letterSpacing: '0.04em' }}>
                  PREPARING MODERN EXPERIENCE
                </Box>
              </motion.div>
            </Box>
          )}
        </AnimatePresence>
        <WebFooter />
        <AiAdmissionsChatbot />
        <Dialog
          open={showAdmissionsPrompt}
          onClose={() => setShowAdmissionsPrompt(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignRoundedIcon sx={{ color: '#059669' }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                Admissions Open
              </Typography>
            </Box>
            <IconButton onClick={() => setShowAdmissionsPrompt(false)} size="small">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 0.6 }}>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
              Share your details and the admissions team can get back to you.
            </Typography>
            <Box component="form" onSubmit={handleAdmissionsSubmit} sx={{ display: 'grid', gap: 1.25 }}>
              <TextField
                label="Parent Name"
                size="small"
                value={admissionsForm.parentName}
                onChange={(event) => handleAdmissionsFormChange('parentName', event.target.value)}
                required
              />
              <TextField
                label="Student Name"
                size="small"
                value={admissionsForm.studentName}
                onChange={(event) => handleAdmissionsFormChange('studentName', event.target.value)}
              />
              <TextField
                label="Class Applying For"
                size="small"
                value={admissionsForm.grade}
                onChange={(event) => handleAdmissionsFormChange('grade', event.target.value)}
              />
              <TextField
                label="Phone Number"
                size="small"
                value={admissionsForm.phone}
                onChange={(event) => handleAdmissionsFormChange('phone', event.target.value)}
                required
              />
              <TextField
                label="Email Address"
                size="small"
                type="email"
                value={admissionsForm.email}
                onChange={(event) => handleAdmissionsFormChange('email', event.target.value)}
              />
              <TextField
                label="Message (Optional)"
                size="small"
                multiline
                minRows={3}
                value={admissionsForm.message}
                onChange={(event) => handleAdmissionsFormChange('message', event.target.value)}
              />
              <DialogActions sx={{ px: 0, pb: 0, pt: 1 }}>
                <Button onClick={() => setShowAdmissionsPrompt(false)}>
                  Maybe Later
                </Button>
                <Button type="submit" variant="contained" disabled={admissionsSubmitting}>
                  {admissionsSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogActions>
            </Box>
          </DialogContent>
        </Dialog>
        <Snackbar
          open={snackbarState.open}
          autoHideDuration={5000}
          onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbarState.severity}
            onClose={() => setSnackbarState((prev) => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {snackbarState.message}
          </Alert>
        </Snackbar>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PublicLayout;
